<template>
  <div>
    <!-- Navigation Components -->
    <TopBar
      :language="language"
      :languageList="languageList"
      :activeButton="''"
      :currentPage="'cases'"
      @change-language="changeLanguage"
      @scroll-to-target="handleNavigation"
      ref="navbar"
    />
    <MobileNavigation
      :languageList="languageList"
      :currentPage="'cases'"
      @change-language="changeLanguage"
      @scroll-to-target="handleNavigation"
    />

    <!-- Cases Content -->
    <div class="cases-bg py-16 pt-32">
      <v-container class="max-width-container">
        <!-- 麵包屑 -->
        <v-breadcrumbs class="mb-6 px-0" :items="breadcrumbs" divider=">"></v-breadcrumbs>

        <!-- 主標與說明 -->
        <div class="text-center mb-12">
          <div class="hero-section">
            <h1 class="text-h3 font-weight-black mb-4 hero-title">{{ $t("cases") }}</h1>
            <p class="text-h6 grey--text text--darken-1 mb-0 hero-subtitle">{{ subtitle }}</p>
          </div>
        </div>

        <!-- 篩選列 -->
        <div class="filter-search-section mb-10">
          <div class="d-flex align-center flex-wrap">
            <div class="filter-group mr-6">
              <v-chip-group v-model="selectedCategory" mandatory>
                <v-chip v-for="c in categoryList" :key="c.id" :value="c.id" class="ma-3 filter-chip"
                  :class="{ 'filter-chip--active': selectedCategory === c.id }" outlined>
                  {{ c.name }} ({{ c.count }})
                </v-chip>
              </v-chip-group>
            </div>
            <v-spacer></v-spacer>
          </div>
        </div>

        <!-- 結果統計 -->
        <div class="d-flex align-center justify-space-between mb-6">
          <div class="text-h6 font-weight-bold" style="color: #F57F17;">
            {{ $t('cases_title') }}
            <v-chip small color="warning" class="ml-2">{{ filteredCases.length }}</v-chip>
          </div>
        </div>

        <!-- 兩欄卡片格線 -->
        <v-row>
          <v-col v-for="(caseItem, index) in filteredCases" :key="index" cols="12" md="6" class="mb-8 d-flex">
            <v-card class="case-card flex-grow-1 rounded-xl" elevation="0" outlined>
              <!-- 卡片頭部 -->
              <div class="card-header pa-6 pb-4">
                <div class="d-flex align-center justify-space-between">
                  <h3 class="text-h6 font-weight-bold case-title">
                    {{ caseItem.title }}
                  </h3>
                </div>
                <p class="text-caption mt-2 mb-0">{{ caseItem.client }}</p>
              </div>

              <!-- 卡片內容 -->
              <v-card-text class="px-6 pt-0 pb-4">
                <p class="text-body-1 case-summary mb-4 text--secondary">
                  {{ caseItem.summary }}
                </p>

                <!-- 技術標籤 -->
                <div class="d-flex flex-wrap gap-2">
                  <v-chip v-for="(tag, tIdx) in caseItem.tags" :key="tIdx" x-small outlined class="tech-tag"
                    color="grey">
                    {{ tag }}
                  </v-chip>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <v-footer dark padless>
      <v-card class="flex" flat tile>
        <v-card-text class="py-2 white--text text-center" style="background-color: #797c80">
          {{ $t("copyRight") }}{{ new Date().getFullYear() }} —
          {{ $t("company") }}
        </v-card-text>
      </v-card>
    </v-footer>
  </div>
</template>

<script>
import TopBar from "@/components/TopBar.vue";
import MobileNavigation from "@/components/MobileNavigation.vue";
import { getCaseStudies } from "@/data/caseStudies/index.js";

export default {
  name: 'CasesView',
  props: {
    lang: {
      type: String,
      default: "zh-TW",
    },
  },
  data() {
    return {
      language: {
        value: this.lang,
        title: this.lang === "en" ? "English" : "繁體中文",
      },
      languageList: [
        {
          value: "zh-TW",
          title: "繁體中文",
        },
        {
          value: "en-US",
          title: "English",
        },
        {
          value: "ja-JP",
          title: "日本語",
        },
      ],
      selectedCategory: 'all',
    };
  },
  components: {
    TopBar,
    MobileNavigation,
  },
  methods: {
    changeLanguage(lang) {
      let selectedLanguage;

      if (lang !== undefined) {
        if (typeof lang === "number") {
          selectedLanguage = this.languageList[lang] || this.languageList[0];
        } else {
          selectedLanguage =
            this.languageList.find((l) => l.value === lang) ||
            this.languageList[0];
        }
      } else {
        const currentIndex = this.languageList.findIndex(
          (l) => l.value === this.language.value
        );
        const nextIndex = (currentIndex + 1) % this.languageList.length;
        selectedLanguage = this.languageList[nextIndex];
      }

      this.language = selectedLanguage;

      switch (this.language.value) {
        case "zh-TW":
          this.$i18n.locale = "tw";
          break;
        case "en-US":
          this.$i18n.locale = "en";
          break;
        case "ja-JP":
          this.$i18n.locale = "jp";
          break;
      }

      this.$router
        .push({ query: { ...this.$route.query, lang: this.language.value } })
        .catch((err) => { });
    },
    handleNavigation(target) {
      if (target === 'cases') {
        return;
      } else {
        if (this.$route.path !== '/') {
          this.$router.push('/').catch(() => {});
        }
      }
    },
  },
  computed: {
    breadcrumbs() {
      return [
        { text: this.$t('home') || '首頁', disabled: false, to: '/' },
        { text: this.$t('cases') || '案例展示', disabled: true }
      ];
    },
    subtitle() {
      return this.$t('research_subtitle') || '深耕醫療與智慧系統整合，累積豐富實戰經驗';
    },
    allCases() {
      const locale = this.$i18n.locale;
      const localeMap = {
        'tw': 'zh-TW',
        'en': 'en',
        'jp': 'ja'
      };
      const { caseStudies } = getCaseStudies(localeMap[locale] || 'zh-TW');
      return caseStudies;
    },
    categoryList() {
      const locale = this.$i18n.locale;
      const localeMap = {
        'tw': 'zh-TW',
        'en': 'en',
        'jp': 'ja'
      };
      const { categories } = getCaseStudies(localeMap[locale] || 'zh-TW');
      return categories;
    },
    filteredCases() {
      if (this.selectedCategory === 'all') {
        return this.allCases;
      }
      return this.allCases.filter(c => c.category === this.selectedCategory);
    }
  },
  mounted() {
    this.changeLanguage(this.lang);
    this.$nextTick(() => {
      if (this.$wow && typeof this.$wow === 'function') {
        try {
          new this.$wow().init();
        } catch (error) {
          console.warn('WOW.js initialization failed:', error);
        }
      }
    });
  },
};
</script>

<style scoped>

.cases-bg {
  min-height: 100vh;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #f4eddf 100%);
}

/* 篩選標籤樣式 */
.filter-chip {
  transition: all 0.3s ease;
  border-color: #e0e0e0 !important;
  color: #666 !important;
  background-color: #fafafa !important;
}

.filter-chip--active {
  color: #fb8c00 !important;
  border-color: #FF8F00 !important;
  box-shadow: 0 2px 8px rgba(255, 143, 0, 0.3);
}

.filter-chip:hover:not(.filter-chip--active) {
  background-color: #f5f5f5 !important;
  border-color: #bdbdbd !important;
}

/* 卡片樣式 */
.case-card {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;
  border: 1px solid #e1e1e1;
  background: linear-gradient(145deg, #ffffff 0%, #fefefe 100%);
  position: relative;
  overflow: hidden;
}

.case-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  /* background: linear-gradient(90deg, #FF8F00, #FFC107); */
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.case-card:hover::before {
  transform: scaleX(1);
}

.case-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1), 0 6px 20px rgba(0, 0, 0, 0.08);
}

/* 卡片內部元素 */
.card-header {
  background: linear-gradient(135deg, #fafafa 0%, #ffffff 100%);
  border-bottom: 1px solid #f0f0f0;
}

.case-title {
  color: #2c3e50;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.case-summary {
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: #5f6368 !important;
}

.tech-tag {
  font-size: 11px !important;
  height: 24px !important;
  font-weight: 500;
  color: #666 !important;
  border-color: #e0e0e0 !important;
  margin-right: 6px;
  margin-bottom: 6px;
}

.tech-tag:hover {
  background-color: #f5f5f5 !important;
  border-color: #bdbdbd !important;
}

/* 篩選與搜尋區塊 */
.filter-search-section {
  background: rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  padding: 16px 0px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
}

/* gap 工具類別 */
.gap-2>* {
  margin-right: 8px;
  margin-bottom: 8px;
}

.gap-2>*:last-child {
  margin-right: 0;
}

/* 容器與佈局 */
.max-width-container {
  max-width: 1200px;
}

/* Hero 區塊 */
.hero-section {
  padding: 40px 0;
}

.hero-title {
  color: #2c3e50;
  position: relative;
  margin-bottom: 20px !important;
}

.hero-title::after {
  content: '';
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #FF8F00, #FFC107);
  border-radius: 2px;
}

.hero-subtitle {
  color: #5f6368 !important;
  font-weight: 400;
  line-height: 1.5;
  max-width: 600px;
  margin: 0 auto;
}

/* 響應式設計 */
@media (max-width: 960px) {
  .case-card:hover {
    transform: translateY(-4px) scale(1.01);
  }

  .card-header {
    padding: 20px 20px 16px 20px !important;
  }

  .case-summary {
    -webkit-line-clamp: 3;
  }
}
</style>
